import React, { useMemo } from 'react';
import { formatearUsd } from './metricas';
import { clasificacionTerminada, idsPorMotivo, resumenMotivos } from './motivos';
import type { Caso, ClasificacionMotivos } from './tipos';
import { Aviso, CHIP, ChipStatus, btnPrimario, btnSecundario, formatearFecha, inputChicoCls } from './ui';

/**
 * Preselección de editadas con IA: lanzar la clasificación del motivo de las ediciones
 * (fallo de la generación / cambio creativo / ambiguo), el estado del job en curso, el
 * resumen por motivo y los atajos para seleccionar solo los fallos. El poll y la mezcla
 * de motivos en el dataset viven en AnalisisCasos (dueño del dataset): esto muestra y avisa.
 */
const PanelPreseleccion: React.FC<{
  modelos: string[];
  modelo: string;
  onModelo: (m: string) => void;
  /** Las EDITADAS del dataset (el control no se clasifica). */
  casos: Caso[];
  clasificacion: ClasificacionMotivos | null;
  lanzando: boolean;
  error: string | null;
  onClasificar: (forzar: boolean) => void;
  /** Reemplaza la selección de editadas por estos ids. */
  onSeleccionar: (ids: string[]) => void;
}> = ({ modelos, modelo, onModelo, casos, clasificacion, lanzando, error, onClasificar, onSeleccionar }) => {
  const resumen = useMemo(() => resumenMotivos(casos), [casos]);
  const idsFallo = useMemo(() => idsPorMotivo(casos, ['fallo_generacion']), [casos]);
  const idsFalloAmbiguo = useMemo(() => idsPorMotivo(casos, ['fallo_generacion', 'ambiguo']), [casos]);
  const enCurso = clasificacion !== null && !clasificacionTerminada(clasificacion);
  const puede = !lanzando && !enCurso;

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-semibold text-brand-black">Preselección con IA</h3>
        <label className="flex items-center gap-2 text-xs text-gray-500">
          Modelo
          <select value={modelo} onChange={(e) => onModelo(e.target.value)} className={inputChicoCls}>
            {modelos.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => onClasificar(false)} disabled={!puede || resumen.sin_clasificar === 0} className={btnPrimario}>
          {lanzando ? 'Encolando…' : `Clasificar ediciones con IA (${resumen.sin_clasificar} sin clasificar)`}
        </button>
        <button
          type="button"
          onClick={() => onClasificar(true)}
          disabled={!puede || casos.length === 0}
          className={`${btnSecundario} py-1.5 px-3 text-xs`}
          title="Vuelve a clasificar TODAS las editadas del dataset y pisa lo cacheado (gasta de nuevo)"
        >
          Reclasificar todo
        </button>
        {clasificacion && enCurso && (
          <span className="flex items-center gap-2 text-xs text-blue-600">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            {clasificacion.status === 'running' ? 'Clasificando…' : 'En cola…'} {clasificacion.hechas} / {clasificacion.total}
            <ChipStatus status={clasificacion.status} />
            {/* "queued" que no arranca = no hay worker consumiendo la cola (en local hay que correr `npm run dev:worker`). */}
            {clasificacion.status !== 'running' && <span className="text-gray-500">si no arranca, fijate que el worker esté corriendo</span>}
          </span>
        )}
        {clasificacion && !enCurso && (
          <span className="flex items-center gap-2 text-xs text-gray-500">
            <ChipStatus status={clasificacion.status} />
            {clasificacion.hechas} / {clasificacion.total} clasificadas · {clasificacion.modelo} · {formatearFecha(clasificacion.finished_at)}
            {clasificacion.cost_usd !== null && ` · ${formatearUsd(clasificacion.cost_usd)}`}
          </span>
        )}
        {!clasificacion && casos.length > 0 && resumen.sin_clasificar === 0 && (
          <span className="text-xs text-gray-500">Todas las editadas ya tienen motivo.</span>
        )}
      </div>
      <Aviso error={error} />
      <div className="flex flex-wrap items-center gap-2">
        <span className={`${CHIP} bg-red-600 font-semibold text-white`}>Fallo {resumen.fallo}</span>
        <span className={`${CHIP} bg-amber-500 font-semibold text-white`}>Ambiguo {resumen.ambiguo}</span>
        <span className={`${CHIP} bg-gray-200 font-semibold text-gray-700`}>Creativo {resumen.creativo}</span>
        <span className={`${CHIP} border border-gray-300 bg-white text-gray-600`}>Sin clasificar {resumen.sin_clasificar}</span>
        <button type="button" onClick={() => onSeleccionar(idsFallo)} disabled={idsFallo.length === 0} className={`${btnSecundario} py-1.5 px-3 text-xs`}>
          Seleccionar solo fallos
        </button>
        <button type="button" onClick={() => onSeleccionar(idsFalloAmbiguo)} disabled={idsFalloAmbiguo.length === 0} className={`${btnSecundario} py-1.5 px-3 text-xs`}>
          Fallos + ambiguos
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Un modelo de texto lee las instrucciones de edición (no ve las imágenes) y dice si el cliente editó por un fallo de la generación, por
        gusto o si no se sabe. Es una opinión para ordenar la lectura: revisá los ambiguos.
      </p>
    </div>
  );
};

export default PanelPreseleccion;
